import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";

/**
 * LMS - the student's exams + admit card.
 *
 * Shows the exams for the student's course. If an exam fee is set, the student
 * pays it (Razorpay) and the admit card is issued only after the server verifies
 * the payment. A registered exam shows "View Admit Card".
 * (The actual online exam paper is Part 7.)
 */
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Exams() {
  const dispatch = useDispatch();
  const history = useHistory();
  const token = () => localStorage.getItem("studentToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [exams, setExams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // shown in a modal

  const load = async () => {
    try {
      const res = await dispatch(getDataAPI("student/lms/exams", token()));
      setExams(res.data.data || []);
    } catch (e) {
      alert({ error: "Could not load your exams." });
    }
  };
  useEffect(() => { load(); }, []);

  const register = async (confirmBody) => {
    const res = await dispatch(postDataAPI("student/lms/exam/confirm", confirmBody, token()));
    alert({ success: res.data.message || "Admit card issued." });
    load();
  };

  const payAndApply = async (exam) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await dispatch(postDataAPI("student/lms/exam/order", { exam_id: exam.id }, token()));
      const d = res.data.data || {};
      if (d.registered) { alert({ success: "Already registered." }); load(); setBusy(false); return; }
      if (d.free) { await register({ exam_id: exam.id }); setBusy(false); return; }

      const ok = await loadRazorpay();
      if (!ok) { alert({ error: "Could not load the payment window." }); setBusy(false); return; }

      const rzp = new window.Razorpay({
        key: d.key_id,
        order_id: d.order_id,
        amount: d.amount,
        currency: "INR",
        name: "NSVEC",
        description: `Exam fee - ${exam.title}`,
        handler: async (r) => {
          try {
            await register({ exam_id: exam.id, razorpay_payment_id: r.razorpay_payment_id });
          } catch (err) {
            alert({ error: err.response?.data?.message || "Payment made but registration failed - contact support." });
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
      setBusy(false);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not start the payment." });
      setBusy(false);
    }
  };

  const viewAdmit = async (exam) => {
    try {
      const res = await dispatch(getDataAPI(`student/lms/exam/admit/${exam.id}`, token()));
      openAdmitWindow(res.data.data);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Admit card not available." });
    }
  };

  const openAdmitWindow = (a) => {
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { alert({ error: "Allow pop-ups to view the admit card." }); return; }
    const row = (k, v) => `<tr><td style="padding:6px 10px;border:1px solid #ccc;font-weight:600;width:40%">${k}</td><td style="padding:6px 10px;border:1px solid #ccc">${v || "-"}</td></tr>`;
    w.document.write(`
      <html><head><title>Admit Card ${a.admit_no}</title></head>
      <body style="font-family:Arial,sans-serif;padding:24px;color:#111">
        <h2 style="text-align:center;margin:0 0 4px">NSVEC - Admit Card</h2>
        <p style="text-align:center;margin:0 0 16px">National Skill &amp; Vocational Education Council</p>
        <table style="border-collapse:collapse;width:100%">
          ${row("Admit No.", a.admit_no)}
          ${row("Name", a.student.name)}
          ${row("Roll Number", a.student.roll_number)}
          ${row("Date of Birth", a.student.dob)}
          ${row("Course", a.course)}
          ${row("Subject", a.subject)}
          ${row("Centre", a.center)}
          ${row("Exam", a.exam.title)}
          ${row("Exam Date", a.exam.date)}
          ${row("Duration", a.exam.duration_minutes + " minutes")}
          ${row("Total Questions", a.exam.total_questions)}
          ${row("Passing", a.exam.passing_percent + "%")}
        </table>
        <p style="margin-top:28px">Signature: ____________________</p>
        <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
      </body></html>`);
    w.document.close();
  };

  const startExam = (exam) => {
    if (!window.confirm(`Start "${exam.title}"? The timer begins now and cannot be paused.`)) return;
    history.push(`/student/lms-exam/${exam.id}`);
  };

  const viewResult = async (exam) => {
    try {
      const res = await dispatch(getDataAPI(`student/lms/exam/result/${exam.id}`, token()));
      setResult(res.data.data);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Result not available." });
    }
  };

  const actionCell = (x) => {
    // Point 4: council entered marks manually -> exam is done, result is official.
    if (x.manual_result) {
      return <span className="badge badge-success">Completed (result in marksheet)</span>;
    }
    // Point 5: auto exam not open yet (course duration not complete / fees pending).
    if (x.auto && !x.open) {
      return (
        <span className="badge badge-secondary">
          Opens after course completes{x.available_from ? ` (${x.available_from})` : ""}
        </span>
      );
    }
    if (!x.registered) {
      return (
        <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => payAndApply(x)}>
          {Number(x.fee) > 0 ? `Pay ₹${x.fee} & Apply` : "Get Admit Card"}
        </button>
      );
    }
    if (x.result_ready) {
      return (
        <>
          <button className="btn btn-sm btn-info mr-1" onClick={() => viewResult(x)}>View Result</button>
          <button className="btn btn-sm btn-outline-success" onClick={() => viewAdmit(x)}>Admit Card</button>
        </>
      );
    }
    if (x.attempt_status === "submitted") {
      return <span className="badge badge-info">Result awaited</span>;
    }
    if (x.can_start) {
      return (
        <>
          <button className="btn btn-sm btn-success mr-1" onClick={() => startExam(x)}>
            {x.attempt_status === "in_progress" ? "Resume Exam" : "Start Exam"}
          </button>
          <button className="btn btn-sm btn-outline-success" onClick={() => viewAdmit(x)}>Admit Card</button>
        </>
      );
    }
    // Registered but not open yet.
    return (
      <>
        <span className="badge badge-secondary mr-1">Not open yet</span>
        <button className="btn btn-sm btn-outline-success" onClick={() => viewAdmit(x)}>Admit Card</button>
      </>
    );
  };

  return (
    <div style={{ padding: 20 }}>
      <h4 className="mb-3">Exams</h4>
      {exams.length === 0 ? (
        <p className="text-muted">No exams scheduled for your course yet.</p>
      ) : (
        <table className="table table-bordered bg-white">
          <thead>
            <tr>
              <th>Exam</th>
              <th>Subject</th>
              <th>Date</th>
              <th>Duration</th>
              <th>Questions</th>
              <th>Fee</th>
              <th style={{ width: 260 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {exams.map((x) => (
              <tr key={x.id}>
                <td>{x.title}</td>
                <td>{x.subject_name || "Whole course"}</td>
                <td>{x.exam_date || "—"}</td>
                <td>{x.duration_minutes} min</td>
                <td>{x.total_questions}</td>
                <td>{Number(x.fee) > 0 ? `₹${x.fee}` : "Free"}</td>
                <td>{actionCell(x)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal show={!!result} onHide={() => setResult(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Result {result && result.title ? `- ${result.title}` : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {result && result.published ? (
            <table className="table table-bordered mb-0">
              <tbody>
                <tr><th>Total Questions</th><td>{result.total_questions}</td></tr>
                <tr><th>Attempted</th><td>{result.attempted_count}</td></tr>
                <tr><th>Correct</th><td>{result.correct}</td></tr>
                <tr><th>Wrong</th><td>{result.wrong}</td></tr>
                <tr><th>Marks</th><td>{result.marks_obtained} / {result.marks_total}</td></tr>
                <tr><th>Percentage</th><td>{result.percent}%</td></tr>
                <tr>
                  <th>Result</th>
                  <td>{result.passed
                    ? <span className="text-success font-weight-bold">PASS</span>
                    : <span className="text-danger font-weight-bold">FAIL</span>}</td>
                </tr>
                {result.result_date && <tr><th>Result Date</th><td>{result.result_date}</td></tr>}
              </tbody>
            </table>
          ) : (
            <p className="text-muted mb-0">Result is not published yet.</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
